import { cx } from '@flowus/common/cx';
import { $searchParams } from 'src/utils';
import { getReadableFileSizeString } from 'src/editor/utils/size-utils';
import dayjs from 'dayjs';
import { BlockType } from '@next-space/fe-api-idl';
import type { SpacePDFHistoryDTO } from '@next-space/fe-api-idl';
import type { FC, MouseEvent } from 'react';
import { Icon } from 'src/common/components/icon';
import { useGetPageId } from 'src/utils/getPageId';
import { useCurrentSpace } from 'src/hooks/space';
import { useHistory } from 'react-router-dom';
import { Tooltip } from 'src/common/components/tooltip';
import { last } from 'lodash-es';
import { usePickBlock } from 'src/utils/pick-block';
import { segmentsToText } from 'src/editor/utils/editor';
import { InlinePage } from 'src/editor/editor/inline/inline-page';
import { useOpenModal } from 'src/common/components/next-modal';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { useDrive } from 'src/views/main/page-drive/drive-content/use-drive';
import { decodeName } from '@flowus/common/utils';

export const PDFHistoryItem: FC<SpacePDFHistoryDTO> = (item) => {
  const history = useHistory();
  const pageId = useGetPageId();
  const space = useCurrentSpace();
  const block = usePickBlock(item.blockId, ['data', 'status']);
  const openModal = useOpenModal();
  const { openMenuList } = useDrive({ uuid: item.blockId });

  const isBitablePDF = block?.type === BlockType.PAGE;
  const isDefaultFolder = space.setting && block?.parentId === space.setting.pdfFolderId;

  const handleClick = () => {
    let url = `/preview/${item.blockId}`;
    if (item.propertyId) {
      url += `?propertyId=${item.propertyId}&ossName=${encodeURIComponent(item.ossName)}`;
    }
    history.push(url);
  };

  const openMenu = (event: MouseEvent<HTMLButtonElement>) => {
    if (isDefaultFolder) {
      openMenuList(event, true);
      return;
    }

    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'right-start',
      content() {
        return (
          <ListView
            className="next-modal w-[228px] py-[5px]"
            items={[
              {
                type: ListItemType.OPERATION,
                data: {
                  icon: 'IcMenuOpen',
                  title: '打开',
                  onClick: handleClick,
                },
              },
              {
                type: ListItemType.OPERATION,
                data: {
                  icon: 'IcMenuOpenPage',
                  title: '打开文件所属页面',
                  onClick: () => {
                    history.push(`/${isBitablePDF ? item.blockId : block?.parentId}`);
                  },
                },
              },
            ]}
          />
        );
      },
    });
  };

  let fileName = decodeName(last(item.ossName.split('/')) ?? '');
  if (!isBitablePDF) {
    fileName = segmentsToText(block?.data.segments ?? []);
  }

  let isCurrentPage = pageId === item.blockId;
  if (isBitablePDF) {
    isCurrentPage =
      isCurrentPage &&
      decodeName($searchParams.ossName ?? '') === item.ossName &&
      decodeName($searchParams.propertyId ?? '') === item.propertyId;
  }

  if (!block) return null;
  return (
    <div
      className={cx(
        'rounded px-2 py-4 bg-grey9 flex flex-col mb-4 relative group cursor-pointer hover:bg-black/5',
        isCurrentPage &&
          'border px-[7px] py-[15px] border-active_color bg-active_color_10 hover:bg-active_color_10'
      )}
      onClick={handleClick}
    >
      <Tooltip popup="菜单" className="group-hover:opacity-100 opacity-0 absolute right-1 top-1">
        <button
          className="animate-hover mx-1 flex p-1 items-center justify-center rounded text-grey3"
          onClick={(event) => {
            event.stopPropagation();
            openMenu(event);
          }}
        >
          <Icon size="middle" name="IcMore" />
        </button>
      </Tooltip>
      <div className="flex items-center">
        <Icon name="MIcPdf" size="middle" className="mr-2 text-grey3 flex-shrink-0" />
        <p className="text-t2 text-black line-clamp-2">{fileName ?? ''}</p>
      </div>
      <div
        className={cx(
          'overflow-hidden pl-7 flex justify-between mt-2 text-t4 text-grey3',
          !isDefaultFolder && block && 'mb-1'
        )}
      >
        {item.size > 0 && <span>{getReadableFileSizeString(item.size)}</span>}
        <span>{dayjs(item.createdAt).format('YYYY-MM-DD')}</span>
      </div>
      {!isDefaultFolder && block && (
        <InlinePage
          uuid={isBitablePDF ? block.uuid : block?.parentId}
          className="text-t4 text-grey3 ml-7"
          autoIconSize={true}
        />
      )}
    </div>
  );
};
